import type { MailboxInfo } from "owa-client-types";
import { getMailboxTypeForBizchatTelemetry } from "owa-bizchat-account-utils";
import { OKRCategories } from "./types/OKRCategories";
import type { PrioritizeAction, PrioritizeTelemetryEvent, PrioritizeTelemetryEntryPoint, } from "./types/scenarios/Prioritize";
import { type EventArea, logCoreUsage } from "owa-analytics";

function getOKRCategoryForEvent(event: PrioritizeTelemetryEvent, customData: {
    [key: string]: any;
}): OKRCategories {
    switch (event) {
        case "OpenInbox":
        case "OpenSort":
        case "OpenFilter":
        case "PrioritizeButtonRendered":
            return OKRCategories.Seen;
        case "EnabledChanged":
        case "RulesCountChanged":
        case "SummaryCardExpanded":
        case "PrioritizeSummary_Clicked":
        case "PrioritizeViewOpened":
            return OKRCategories.Tried;
        case "PrioritizeTriggered":
            return OKRCategories.TriedInitiate;
        case "PrioritizeViewClosed":
            return OKRCategories.Closed;
        case "PrioritizeGenerationDiscarded":
            return OKRCategories.Discard;
        case "SummaryCardContentCopied":
            return OKRCategories.Kept;
        case "PrioritizeNudgeDismissed":
        case "PrioritizeNudgeDontShowAgain":
        case "PrioritizeNudgeCustomizeInstructions":
        case "PrioritizeGenerationStopped":
        case "PrioritizeNudgeRendered":
        case "PrioritizeGenerationReady":
        case "PrioritizeGenerationFailed":
        case "PrioritizeGenerationRetried":
            return OKRCategories.Analysis;
        case "PrioritizeFeedback":
            return customData.upvote ? OKRCategories.ThumbsUp : OKRCategories.ThumbsDown;
        default:
            return OKRCategories.None;
    }
}

function getEntryPointForEvent(event: PrioritizeTelemetryEvent): PrioritizeTelemetryEntryPoint | undefined {
    switch (event) {
        case "OpenInbox":
        case "OpenSort":
        case "OpenFilter":
        case "PrioritizeButtonRendered":
        case "PrioritizeViewOpened":
        case "PrioritizeTriggered":
        case "PrioritizeNudgeRendered":
        case "PrioritizeNudgeDismissed":
        case "PrioritizeNudgeDontShowAgain":
        case "PrioritizeNudgeCustomizeInstructions":
        case "PrioritizeGenerationStopped":
        case "PrioritizeGenerationReady":
        case "PrioritizeGenerationDiscarded":
        case "PrioritizeViewClosed":
        case "PrioritizeGenerationFailed":
        case "PrioritizeGenerationRetried":
        case "PrioritizeFeedback":
            return "MessageList";
        case "EnabledChanged":
        case "RulesCountChanged":
            return "Settings";
        case "SummaryCardExpanded":
        case "SummaryCardContentCopied":
        case "PrioritizeSummary_Clicked":
            return "ReadingPane";
        default:
            return undefined;
    }
}

const PRIORITIZE_ON_DEMAND_SCENARIO = "PrioritizeOnDemand";
const PRIORITIZE_SCENARIO = "Prioritize";

function getScenarioForEvent(event: PrioritizeTelemetryEvent, customData: {
    [key: string]: any;
}): string {
    switch (event) {
        case "RulesCountChanged":
        case "PrioritizeTriggered":
        case "PrioritizeButtonRendered":
        case "PrioritizeNudgeRendered":
        case "PrioritizeNudgeDismissed":
        case "PrioritizeNudgeDontShowAgain":
        case "PrioritizeNudgeCustomizeInstructions":
        case "PrioritizeViewOpened":
        case "PrioritizeGenerationStopped":
        case "PrioritizeGenerationReady":
        case "PrioritizeGenerationDiscarded":
        case "PrioritizeViewClosed":
        case "PrioritizeGenerationFailed":
        case "PrioritizeGenerationRetried":
        case "PrioritizeFeedback":
            return customData.isPrioritizeOnDeliveryOn
                ? PRIORITIZE_SCENARIO
                : PRIORITIZE_ON_DEMAND_SCENARIO;
        default:
            return PRIORITIZE_SCENARIO;
    }
}

function getActionForEvent(event: PrioritizeTelemetryEvent): PrioritizeAction {
    switch (event) {
        case "PrioritizeButtonRendered":
            return "Seen";
        case "PrioritizeNudgeRendered":
            return "Suggest";
        case "PrioritizeTriggered":
            return "Generate";
        case "PrioritizeGenerationReady":
            return "Ready";
        case "PrioritizeGenerationFailed":
            return "Failed";
        case "PrioritizeGenerationRetried":
            return "Retried";
        case "PrioritizeFeedback":
            return "Feedback";
        case "PrioritizeViewOpened":
            return "Open";
        case "PrioritizeViewClosed":
            return "Close";
        case "PrioritizeGenerationStopped":
        case "PrioritizeGenerationDiscarded":
        case "PrioritizeNudgeDismissed":
            return "UserDismiss";
        case "PrioritizeNudgeDontShowAgain":
            return "OptOut";
        case "PrioritizeNudgeCustomizeInstructions":
            return "OpenSettings";
        case "EnabledChanged":
        case "RulesCountChanged":
            return "SettingChange";
        case "PrioritizeSummary_Clicked":
        case "SummaryCardExpanded":
            return "Expand";
        case "SummaryCardContentCopied":
            return "Copy";
        default:
            return "Other";
    }
}

function getVariantDataForEvent(event: PrioritizeTelemetryEvent): {
    [key: string]: string;
} {
    switch (event) {
        case "EnabledChanged":
            return { SettingName: "Enabled" };
        case "RulesCountChanged":
            return { SettingName: "RulesCount" };
        case "OpenFilter":
            return { NavigationTarget: "Filter" };
        case "OpenInbox":
            return { NavigationTarget: "Inbox" };
        case "OpenSort":
            return { NavigationTarget: "Sort" };
        case "PrioritizeGenerationDiscarded":
            return { DismissReason: "Discarded" };
        case "PrioritizeGenerationStopped":
            return { DismissReason: "Stopped" };
        case "PrioritizeSummary_Clicked":
            return { SummaryAction: "Clicked" };
        case "SummaryCardContentCopied":
            return { SummaryAction: "Copied" };
        case "SummaryCardExpanded":
            return { SummaryAction: "Expanded" };
        default:
            return {};
    }
}

export function logPrioritizeTelemetryEvent(event: PrioritizeTelemetryEvent, mailboxInfo: MailboxInfo, customData: {
    [key: string]: any;
} = {}): void {
    const scenario = getScenarioForEvent(event, customData);
    const canonicalTelemetryData = {
        OKRCategory: getOKRCategoryForEvent(event, customData),
        EntryPoint: getEntryPointForEvent(event),
        Action: getActionForEvent(event),
        Scenario: scenario,
        MailboxProvider: mailboxInfo.mailboxProvider,
        MailboxType: getMailboxTypeForBizchatTelemetry(mailboxInfo),
    };
    const legacyTelemetryData = {
        ...canonicalTelemetryData,
        ...customData,
    };
    
    logCoreUsage("AIStandardTelemetry", mailboxInfo, {
        CopilotEventName: event,
        ...legacyTelemetryData,
    }, { isScorecardEvent: false, eventArea: "AI" });
    
    const distinctTelemetryData = {
        ...customData,
        ...canonicalTelemetryData,
        ...getVariantDataForEvent(event),
    };
    if (scenario === PRIORITIZE_ON_DEMAND_SCENARIO) {
        logCoreUsage("AIStandardTelemetry_PrioritizeOnDemand", mailboxInfo, distinctTelemetryData, {
            isScorecardEvent: true,
            eventArea: "AI",
        });
    }
    else {
        logCoreUsage("AIStandardTelemetry_Prioritize", mailboxInfo, distinctTelemetryData, {
            isScorecardEvent: true,
            eventArea: "AI",
        });
    }
}
